// Who's online on this client/site, which page they're on and which record they have open.
// Supabase Realtime Presence on a private channel per site (pmc3:presence:<site id>).
import { createContext, useContext, useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import { useLocation } from 'react-router';
import type { RealtimeChannel } from '@supabase/supabase-js';
import { supabase } from './supabase';
import { useApp } from './app';

export type Here = {
  uid: string;
  name: string;
  path: string;
  record: string | null; // "table:id" when a record is open
  status: 'active' | 'away';
  at: string;
};

type Ctx = {
  others: Here[];
  /** the site's presence channel is joined (V0.4.15 live block heartbeat) */
  joined: boolean;
  setRecord: (r: string | null) => void;
  onPage: (path: string) => Here[];
  editing: (table: string, id: string | undefined) => Here[];
};

const PresenceCtx = createContext<Ctx>({ others: [], joined: false, setRecord: () => {}, onPage: () => [], editing: () => [] });
export const usePresence = () => useContext(PresenceCtx);

export function PresenceProvider({ children }: { children: ReactNode }) {
  const { session, profile, siteId } = useApp();
  const loc = useLocation();
  const [others, setOthers] = useState<Here[]>([]);
  const [record, setRecord] = useState<string | null>(null);
  const [status, setStatus] = useState<'active' | 'away'>(document.visibilityState === 'visible' ? 'active' : 'away');
  const chRef = useRef<RealtimeChannel | null>(null);
  const joined = useRef(false);
  const [isJoined, setJoined] = useState(false);
  const uid = session?.user.id;
  const name = profile?.full_name || session?.user.email || 'Someone';

  const me = useMemo<Here | null>(
    () => (uid ? { uid, name, path: loc.pathname, record, status, at: new Date().toISOString() } : null),
    [uid, name, loc.pathname, record, status],
  );

  const meRef = useRef(me);
  meRef.current = me;

  // join the site channel
  useEffect(() => {
    if (!uid || !siteId) return;
    let alive = true;
    const ch = supabase.channel(`pmc3:presence:${siteId}`, { config: { private: true, presence: { key: uid } } });
    chRef.current = ch;
    ch.on('presence', { event: 'sync' }, () => {
      const state = ch.presenceState<Here>();
      const list: Here[] = [];
      for (const [key, metas] of Object.entries(state)) {
        if (key === uid) continue;
        const latest = [...metas].sort((a, b) => (a.at < b.at ? 1 : -1))[0];
        if (latest) list.push(latest);
      }
      if (alive) setOthers(list.sort((a, b) => a.name.localeCompare(b.name)));
    });
    void supabase.realtime.setAuth().then(() =>
      ch.subscribe((s) => {
        joined.current = s === 'SUBSCRIBED';
        if (alive) setJoined(s === 'SUBSCRIBED');
        if (s === 'SUBSCRIBED' && meRef.current) void ch.track(meRef.current);
      }),
    );
    return () => {
      alive = false;
      joined.current = false;
      setJoined(false);
      chRef.current = null;
      setOthers([]);
      void supabase.removeChannel(ch);
    };
  }, [uid, siteId]);

  // keep my status current (and refresh every 30 s as a heartbeat)
  useEffect(() => {
    if (me && chRef.current && joined.current) void chRef.current.track(me);
  }, [me]);
  useEffect(() => {
    const t = setInterval(() => {
      if (meRef.current && chRef.current && joined.current) void chRef.current.track({ ...meRef.current, at: new Date().toISOString() });
    }, 30000);
    const vis = () => setStatus(document.visibilityState === 'visible' ? 'active' : 'away');
    document.addEventListener('visibilitychange', vis);
    return () => {
      clearInterval(t);
      document.removeEventListener('visibilitychange', vis);
    };
  }, []);

  const value = useMemo<Ctx>(
    () => ({
      others,
      joined: isJoined,
      setRecord,
      onPage: (path) => others.filter((o) => o.path === path),
      editing: (table, id) => (id ? others.filter((o) => o.record === `${table}:${id}`) : []),
    }),
    [others, isJoined],
  );

  return <PresenceCtx.Provider value={value}>{children}</PresenceCtx.Provider>;
}

export function initials(name: string): string {
  return (
    name
      .split(/[\s@.]+/)
      .filter(Boolean)
      .slice(0, 2)
      .map((p) => p[0]!.toUpperCase())
      .join('') || '?'
  );
}
